<div class="h-full flex flex-col">
  <div class="flex-1 flex flex-col relative">

    <!-- Error State -->
    <div *ngIf="error$ | async as error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
      <p class="font-semibold">Error:</p>
      <p>{{ error }}</p>
    </div>

    <!-- Toolbar: Period + Sort (always rendered first so it stays anchored
         across loading/content swaps) -->
    <div class="flex items-center justify-end gap-3 mb-3">
      <app-reports-date-range
        [value]="currentDateRange"
        (dateRangeChanged)="onDateRangeChanged($event)">
      </app-reports-date-range>
      <app-reports-sort
        [value]="currentSort"
        [choices]="sortChoices"
        (sortChanged)="onSortChanged($event)">
      </app-reports-sort>
    </div>

    <!-- Content (always rendered so the table header stays visible during
         loading; the spinner is layered inside each table wrapper, matching
         the pattern used on the Contacts page) -->
    <div>
      <!-- Mobile Layout (Horizontal scroll only) -->
      <div class="sm:hidden">
        <div class="rounded-xl border border-gray-200 overflow-hidden relative">
          <div class="overflow-x-auto">
            <table class="min-w-full border-b border-gray-200">
              <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Company</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Contact</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Actions</th>
              </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
              <tr *ngFor="let company of paginatedCompanies">
                <td class="py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap text-gray-900">
                  <div class="flex items-center space-x-3">
                    <div class="size-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <img *ngIf="company.logo_url; else mobileCompanyInitial"
                           [src]="company.logo_url"
                           [alt]="(company.name || '') + ' logo'"
                           class="size-full object-cover"
                           (error)="$any($event.target).style.display = 'none'" />
                      <ng-template #mobileCompanyInitial>
                        <span class="text-primary font-medium text-sm">{{ company.name?.charAt(0).toUpperCase() }}</span>
                      </ng-template>
                    </div>
                    <div>
                      <div class="font-medium">{{ company.name }}</div>
                      <div class="text-xs text-gray-500">{{ company.url || 'No domain' }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                  <span *ngIf="company.contact?.full_name" class="text-gray-900">{{ company.contact.full_name }}</span>
                  <span *ngIf="!company.contact?.full_name" class="text-gray-400">-</span>
                </td>
                <td class="py-4 pr-4 pl-3">
                  <div class="flex items-center space-x-1">
                    <!-- View -->
                    <button type="button" (click)="viewCompany(company)" class="bg-gray-100 hover:opacity-90 p-2 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-gray-500">
                        <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                        <path fill-rule="evenodd" d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" clip-rule="evenodd" />
                      </svg>
                    </button>

                    <!-- Edit -->
                    <button type="button" (click)="editCompany(company)" class="bg-gray-100 hover:opacity-90 p-2 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-gray-500">
                        <path d="m2.695 14.762-1.262 3.155a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.886L17.5 5.501a2.121 2.121 0 0 0-3-3L3.58 13.419a4 4 0 0 0-.885 1.343Z" />
                      </svg>
                    </button>

                    <!-- Delete -->
                    <button type="button" (click)="deleteCompany(company)" class="bg-red-50 hover:opacity-90 p-2 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-red-500">
                        <path fill-rule="evenodd" d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z" clip-rule="evenodd" />
                      </svg>
                    </button>

                    <!-- Add Deal for this company -->
                    <button type="button"
                            (click)="openAddDealForCompany(company, $event)"
                            class="!ml-8 inline-flex items-center bg-primary/10 hover:opacity-90 text-primary text-xs font-medium whitespace-nowrap px-3 py-1.5 rounded-full">
                      Add deal
                    </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- Loading State for Mobile -->
          <div *ngIf="loading$ | async">
            <div class="text-center py-12">
              <svg class="animate-spin h-6 w-6 text-blue-royal mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          </div>

          <!-- Empty State for Mobile -->
          <div *ngIf="!(loading$ | async) && paginatedCompanies.length === 0">
            <div class="text-center py-12">
              <h3 class="text-sm font-medium text-gray-900">No companies found</h3>
              <p class="mt-1 text-sm text-gray-500">Get started by adding a new company.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop Layout (With internal scroll) -->
      <div class="hidden sm:block sm:flex-1 sm:flex sm:flex-col">
        <!-- Table Container with Border -->
        <div class="rounded-xl border border-gray-200 overflow-hidden flex flex-col relative" [style.height.px]="tableHeight">

          <!-- Scrollable Container -->
          <div class="flex-1 overflow-y-scroll custom-scroll">
            <table class="min-w-full divide-y divide-gray-200 border-b border-gray-200">
              <!-- Fixed Header -->
              <thead class="bg-gray-50 sticky top-0 z-20">
              <tr>
                <th scope="col" class="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-700 sm:pl-6 bg-gray-50">Company</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 bg-gray-50">Contact</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 bg-gray-50">Domain</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 bg-gray-50">Lifecycle Stage</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 bg-gray-50">Contacts</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 bg-gray-50 w-56">Actions</th>
              </tr>
              </thead>
              <!-- Table Body -->
              <tbody class="divide-y divide-gray-200 bg-white">
              <tr *ngFor="let company of paginatedCompanies">
                <td class="py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-6">
                  <div class="flex items-center space-x-3">
                    <div class="size-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <img *ngIf="company.logo_url; else desktopCompanyInitial"
                           [src]="company.logo_url"
                           [alt]="(company.name || '') + ' logo'"
                           class="size-full object-cover"
                           (error)="$any($event.target).style.display = 'none'" />
                      <ng-template #desktopCompanyInitial>
                        <span class="text-primary font-medium">{{ company.name?.charAt(0).toUpperCase() }}</span>
                      </ng-template>
                    </div>
                    <span>{{ company.name }}</span>
                  </div>
                </td>
                <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                  <span *ngIf="company.contact?.full_name" class="text-gray-900">{{ company.contact.full_name }}</span>
                  <span *ngIf="!company.contact?.full_name" class="text-gray-400">-</span>
                </td>
                <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                  <a *ngIf="company.url" [href]="'https://' + company.url" target="_blank" class="text-primary hover:underline">
                    {{ company.url }}
                  </a>
                  <span *ngIf="!company.url" class="text-gray-400">No domain</span>
                </td>
                <td class="px-3 py-4 text-sm whitespace-nowrap">
                    <span [ngClass]="getLifecycleStageClass(company.lifecycle_stage)" class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium">
                      {{ company.lifecycle_stage || 'Unknown' }}
                    </span>
                </td>
                <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                  <!-- Show contacts if company has any -->
                  <div *ngIf="company.company_contacts && company.company_contacts.length > 0" class="flex -space-x-2 overflow-visible">
                    <div *ngFor="let companyContact of company.company_contacts.slice(0, 3)"
                         class="size-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 relative cursor-pointer z-10 overflow-hidden"
                         [matTooltip]="getContactFullName(companyContact.contact)"
                         matTooltipPosition="above"
                         matTooltipClass="custom-tooltip">
                      <img *ngIf="companyContact.contact?.report?.picture; else contactInitial"
                           [src]="companyContact.contact.report.picture"
                           [alt]="getContactFullName(companyContact.contact)"
                           class="size-full object-cover"
                           (error)="$any($event.target).style.display = 'none'" />
                      <ng-template #contactInitial>
                        <span class="text-primary text-sm font-medium">
                          {{ getContactInitials(companyContact.contact) }}
                        </span>
                      </ng-template>
                    </div>

                    <!-- +N more indicator -->
                    <div *ngIf="company.company_contacts.length > 3"
                         class="size-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 relative cursor-pointer z-10"
                         [matTooltip]="getExtraContactsText(company.company_contacts.slice(3))"
                         matTooltipPosition="above"
                         matTooltipClass="custom-tooltip">
                      <span class="text-primary text-xs font-medium">+{{ company.company_contacts.length - 3 }}</span>
                    </div>
                  </div>

                  <!-- Empty state for companies with no linked contacts -->
                  <span *ngIf="!company.company_contacts || company.company_contacts.length === 0" class="text-gray-400">
                    -
                  </span>
                </td>
                <td class="py-4 pr-4 pl-3 sm:pr-6 w-56">
                  <div class="flex items-center space-x-1">
                    <!-- View -->
                    <button type="button" (click)="viewCompany(company)" class="bg-gray-100 hover:opacity-90 p-2 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-gray-500">
                        <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                        <path fill-rule="evenodd" d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" clip-rule="evenodd" />
                      </svg>
                    </button>

                    <!-- Edit -->
                    <button type="button" (click)="editCompany(company)" class="bg-gray-100 hover:opacity-90 p-2 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-gray-500">
                        <path d="m2.695 14.762-1.262 3.155a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.886L17.5 5.501a2.121 2.121 0 0 0-3-3L3.58 13.419a4 4 0 0 0-.885 1.343Z" />
                      </svg>
                    </button>

                    <!-- Delete -->
                    <button type="button" (click)="deleteCompany(company)" class="bg-red-50 hover:opacity-90 p-2 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-red-500">
                        <path fill-rule="evenodd" d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z" clip-rule="evenodd" />
                      </svg>
                    </button>

                    <!-- Add Deal for this company -->
                    <button type="button"
                            (click)="openAddDealForCompany(company, $event)"
                            class="!ml-8 inline-flex items-center bg-primary/10 hover:opacity-90 text-primary text-xs font-medium whitespace-nowrap px-3 py-1.5 rounded-full">
                      Add deal
                    </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- Loading State Overlay -->
          <div *ngIf="loading$ | async" class="absolute inset-0 flex items-center justify-center bg-white">
            <div class="text-center">
              <svg class="animate-spin h-6 w-6 text-blue-royal mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          </div>

          <!-- Empty State Overlay -->
          <div *ngIf="!(loading$ | async) && paginatedCompanies.length === 0" class="absolute inset-0 flex items-center justify-center bg-white">
            <div class="text-center">
              <h3 class="text-sm font-medium text-gray-900">No companies found</h3>
              <p class="mt-1 text-sm text-gray-500">Get started by creating a new company.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div *ngIf="!(loading$ | async) && paginatedCompanies.length > 0" class="mt-3 flex items-center justify-between bg-white dark:bg-indigo-custom">
      <app-crm-pagination (perPageChanged)="onPerPageChanged($event)" (pageChanged)="onPageChanged($event)" [pagination]="pagination" class="w-full"></app-crm-pagination>
    </div>

  </div>

  <!-- Add Company Modal -->
  <app-add-company-modal
    [isVisible]="showAddCompanyModal"
    [contacts]="(contactsLookup$ | async) || []"
    [company]="selectedCompany"
    [mode]="modalMode"
    (close)="onCloseCompanyModal()"
    (save)="onSaveCompany($event)"
    (deleteCompanyContact)="onDeleteCompanyContact($event)">
  </app-add-company-modal>

  <!-- View Company Modal -->
  <app-view-company-modal
    [isVisible]="showViewCompanyModal"
    [companyId]="selectedCompanyId"
    (close)="onCloseViewCompanyModal()">
  </app-view-company-modal>

  <!-- Add Deal Modal (opened from a company row's "Add deal" action) -->
  <app-add-deal-modal
    [isVisible]="showAddDealModal"
    [contacts]="(contactsLookup$ | async) || []"
    [companies]="(companiesLookup$ | async) || []"
    [mode]="'create'"
    [preselectedCompanyId]="preselectedCompanyIdForDeal"
    (close)="onCloseAddDealModal()"
    (save)="onSaveDeal($event)">
  </app-add-deal-modal>
</div>
